// src/pages/AuditLogs/components/AuditLogsPagination.tsx
// Pagination component for Audit Logs with page navigation and page size selector

import React from 'react';
import { Row, Col, Input, Pagination, PaginationItem, PaginationLink } from 'reactstrap';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { setPage, setPagination } from 'slices/auditLogs/reducer';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('audit_logs');

const AuditLogsPagination: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  
  const { pagination } = useSelector((state: any) => state.AuditLogs);
  const { currentPage, pageSize, totalItems, totalPages } = pagination;
  
  // Calculate visible page numbers
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisible = 5;
    
    if (totalPages <= maxVisible) {
      // Show all pages if total is small
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show first page
      pages.push(1);
      
      if (currentPage > 3) {
        pages.push('...');
      }
      
      // Show pages around current page
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      
      if (currentPage < totalPages - 2) {
        pages.push('...');
      }
      
      // Show last page
      pages.push(totalPages);
    }
    
    return pages;
  };
  
  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      dispatch(setPage(page));
    }
  };
  
  const handlePageSizeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPageSize = Number(e.target.value);
    dispatch(setPagination({
      pageSize: newPageSize,
      currentPage: 1, // Reset to first page
      totalPages: Math.ceil(totalItems / newPageSize)
    }));
  };
  
  if (totalItems === 0) {
    return null;
  }
  
  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalItems);
  
  return (
    <Row className="align-items-center mt-3">
      {/* Left: Pagination Info & Page Size */}
      <Col sm={6}>
        <div className="d-flex align-items-center gap-3">
          {/* Page Size Selector */}
          <div className="d-flex align-items-center">
            <span className="text-muted me-2">{st('hien_thi_text')}</span>
            <Input
              type="select"
              value={pageSize}
              onChange={handlePageSizeChange}
              className="form-select form-select-sm"
              style={{ width: 'auto' }}
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </Input>
            <span className="text-muted ms-2">{st('ban_ghi_text')}</span>
          </div>
          
          {/* Pagination Info */}
          {/* <div className="text-muted">
            {t('', {
              from: startItem,
              to: endItem,
              total: totalItems
            }) || `Showing ${startItem} to ${endItem} of ${totalItems} entries`}
          </div> */}
        </div>
      </Col>
      
      {/* Right: Pagination Buttons */}
      <Col sm={6}>
        <div className="d-flex justify-content-end">
          <Pagination className="pagination-separated mb-0">
            {/* Previous Button */}
            <PaginationItem disabled={currentPage === 1}>
              <PaginationLink
                previous
                onClick={() => handlePageChange(currentPage - 1)}
              >
                <i className="ri-arrow-left-line"></i>
              </PaginationLink>
            </PaginationItem>
            
            {/* Page Numbers */}
            {getPageNumbers().map((page, index) => (
              <PaginationItem
                key={index}
                active={page === currentPage}
                disabled={page === '...'}
              >
                <PaginationLink
                  onClick={() => typeof page === 'number' && handlePageChange(page)}
                >
                  {page}
                </PaginationLink>
              </PaginationItem>
            ))}
            
            {/* Next Button */}
            <PaginationItem disabled={currentPage === totalPages}>
              <PaginationLink
                next
                onClick={() => handlePageChange(currentPage + 1)}
              >
                <i className="ri-arrow-right-line"></i>
              </PaginationLink>
            </PaginationItem>
          </Pagination>
        </div>
      </Col>
    </Row>
  );
};

export default AuditLogsPagination;